import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceCreateContractEndpoint,
  useCounterpartServiceGetContractEndpoint,
  useCounterpartServiceGetContractEndpointKey,
  useCounterpartServiceGetContractListEndpointKey,
  useCounterpartServiceGetCounterpartEndpoint,
  useCounterpartServiceUpdateContractEndpoint,
  useTemplatesServiceCreateSystemTemplateEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import {
  FormFooter,
  LoadingScreen,
  message,
  PageBreadcrumbs
} from 'src/components'
import { TemplateTitleModal } from 'src/features/counterParts/components'
import { useFileHandler } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { OtherInstitutionsAddContractForm } from '../components'

export type UploadResponse = {
  createdResources: { id: string }[]
}

export const OtherInstitutionAddContract = () => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { id, contractId } = useParams()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const { data: institution } = useCounterpartServiceGetCounterpartEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const { setExistingFiles, onFileChange, saveFiles, isUploading } =
    useFileHandler({
      form,
      folder: `institution-${id}`
    })

  const onToggleModal = () => setIsModalOpen((prev) => !prev)

  const onMutationSuccess = () => {
    const queryKey = contractId
      ? [useCounterpartServiceGetContractEndpointKey, String(contractId)]
      : [useCounterpartServiceGetContractListEndpointKey]
    queryClient.invalidateQueries({ queryKey })
  }

  const { mutate } = useCounterpartServiceCreateContractEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('counterparts.contact_created'))
      onBackClick()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: edit } = useCounterpartServiceUpdateContractEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('common.save_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: createTemplate } =
    useTemplatesServiceCreateSystemTemplateEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('counterparts.template_created'))
        onToggleModal()
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { data, isLoading } = useCounterpartServiceGetContractEndpoint(
    {
      id: String(contractId)
    },
    [String(contractId)],
    {
      enabled: !!contractId
    }
  )

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.institutions.details(id))
  }

  const onSubmit = async ({
    createNewContract
  }: {
    createNewContract: boolean
  }) => {
    const values = await form.validateFields()
    const updatedFiles = await saveFiles()

    const payload = {
      beneficiaryId:
        data?.beneficiary?.name === values?.beneficiaryId
          ? data?.beneficiary?.id
          : values?.beneficiaryId,
      contractNumber: String(values.contractNumber),
      signedOn: dayjs(values.signedOn).format(yearMonthDaySlash),
      startDate: dayjs(values.startDate).format(yearMonthDaySlash),
      endDate: dayjs(values.endDate).format(yearMonthDaySlash),
      description: values.description,
      counterpartId: String(id),
      mediaFilesIds: updatedFiles,
      fundingSourceId: values?.fundingSourceId
    }

    if (createNewContract) {
      mutate({
        requestBody: payload
      })
    } else {
      contractId
        ? edit({
            id: String(contractId),
            requestBody: payload
          })
        : createTemplate({
            requestBody: {
              name: values.template_name,
              isPublic: true,
              description: values.template_description,
              content: payload
            }
          })
    }
  }

  useEffect(() => {
    if (contractId && data) {
      const existingFileIds = data?.files
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]
      setExistingFiles(existingFileIds)

      form.setFieldsValue({
        beneficiaryId: data?.beneficiary?.id,
        beneficiaryName: data?.beneficiary?.name,
        contractNumber: data?.contractNumber,
        description: data?.description,
        signedOn: toDayjs(data?.signedOn),
        startDate: toDayjs(data?.startDate),
        endDate: toDayjs(data?.endDate),
        fundingSourceId: data?.fundingSourceId,
        docs: existingFileIds.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, contractId])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={
        contractId
          ? String(data?.contractNumber)
          : t('counterparts.add_new_contract')
      }
      items={[
        {
          title: t('menu.other_institutions'),
          path: paths.institutions.root
        },
        ...(institution
          ? [
              {
                title: institution?.name ?? '-',
                path: paths.institutions.details(id)
              }
            ]
          : []),
        {
          title: contractId
            ? String(data?.contractNumber)
            : t('counterparts.add_new_contract')
        }
      ]}
      extra={
        !contractId && (
          <Button
            type="primary"
            onClick={async () => {
              await form.validateFields()
              onToggleModal()
            }}
          >
            {t('counterparts.add_new_template_contract')}
          </Button>
        )
      }
    >
      <Flex vertical gap={24}>
        {contractId && (
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>
        )}

        <Form layout="vertical" form={form} requiredMark={false}>
          <OtherInstitutionsAddContractForm
            data={data}
            onChange={onFileChange}
          />

          <FormFooter
            onCancel={onBackClick}
            onSave={() => onSubmit({ createNewContract: true })}
            saveLabel={t('common.save')}
            saveConfirmTitle={t('common.save')}
            loading={isLoading || isUploading}
          />

          {isModalOpen && (
            <TemplateTitleModal
              onCancel={onToggleModal}
              onOk={() => onSubmit({ createNewContract: false })}
            />
          )}
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
